import "./Footer.css";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaWhatsapp,
} from "react-icons/fa";
import { FiFacebook, FiInstagram } from "react-icons/fi";
import { Link } from "react-router-dom";
import PurchasePopup from "./PurchasePopup";
import React, { useState } from "react";
import { toast } from "react-toastify";
import api from "../api";
import useProducts from "../hooks/useProducts";

function XLogo(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      fill="currentColor"
      viewBox="0 0 16 16"
      {...props}
    >
      <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z" />
    </svg>
  );
}

export default function Footer() {
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);

  const allServices = useProducts();

  const handleSubscribe = async (e) => {
    e.preventDefault();

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      return toast.error("Email is required");
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      return toast.error("Please enter a valid email");
    }

    setIsSubscribing(true);

    try {
      const formData = new FormData();
      formData.append("email", trimmedEmail);

      const res = await api.post("subscribeEmail", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const status = res?.data?.status?.status;

      if (status === "201") {
        toast.success("🎉 You're subscribed!");
        setEmail(""); // no need for setTimeout
      } else if (status === "409") {
        toast.warning("Email already subscribed");
      } else {
        toast.error(res?.data?.status?.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Subscribe Error:", error);
      toast.error("Server error. Please try again.");
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <>
      <footer className="footer-cafe pt-5 pb-3 position-relative">
        <div className="container position-relative">
          <div className="row text-start gy-4">
            {/* Logo + Description */}
            <div className="col-md-4">
              <img
                src={`${process.env.PUBLIC_URL}/logo_cafesero.png`}
                alt="CafeSero Logo"
                className="mb-2"
                style={{ height: "70px", borderRadius: "6px" }}
              />

              <p className="small text-light-cafe fw-bold">
                CAFESERO is not just a coffee brand — it’s a feeling. With every
                cup, we aim to create an experience that energizes your day and
                elevates your everyday moments.
              </p>

              <div className="d-flex gap-3 fs-5 mt-3">
                <a
                  href={process.env.REACT_APP_FACEBOOK_URL || "/"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon"
                  aria-label="Facebook"
                  title="Follow us on Facebook"
                >
                  <FiFacebook size={24} />
                </a>

                <a
                  href={process.env.REACT_APP_TWITTER_URL || "/"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon"
                  aria-label="Twitter (X)"
                  title="Follow us on X (Twitter)"
                >
                  <XLogo size={24} />
                </a>

                <a
                  href={process.env.REACT_APP_INSTAGRAM_URL || "/"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon"
                  aria-label="Instagram"
                  title="Follow us on Instagram"
                >
                  <FiInstagram size={24} />
                </a>

                <a
                  href={process.env.REACT_APP_WHATSAPP_URL || "/"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-icon whatsapp"
                  aria-label="WhatsApp"
                  title="Chat with us on WhatsApp"
                >
                  <FaWhatsapp size={24} />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="col-md-2">
              <h6 className="footer-title">Quick Links</h6>

              <ul className="list-unstyled small">
                <li>
                  <Link to="/about">About Us</Link>
                </li>
                <li>
                  <Link to="/products">Shop</Link>
                </li>
                <li>
                  <Link to="/blogs">Blog</Link>
                </li>
                <li>
                  <Link to="/faqs">FAQ</Link>
                </li>
                <li>
                  <Link to="/contact">Contact</Link>
                </li>
              </ul>
            </div>

            {/* Subscribe Section */}
            <div className="col-md-4">
              <h6 className="footer-title">Subscribe Now</h6>

              <form
                className="subscribe-box d-flex flex-column gap-2"
                onSubmit={handleSubscribe}
              >
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="form-control subscribe-input mt-2"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubscribing} // prevent typing while loading
                  required
                />

                <button
                  type="submit"
                  className="btn btn-primary subscribe-btn mt-2 w-100"
                  disabled={isSubscribing}
                >
                  {isSubscribing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Subscribing...
                    </>
                  ) : (
                    "Subscribe Now"
                  )}
                </button>
              </form>
            </div>

            {/* Contact */}
            <div className="col-md-2">
              <h6 className="footer-title">Contact Info</h6>

              <ul className="list-unstyled small">
                <li className="d-flex mb-2">
                  <FaPhoneAlt className="me-2 text-golden mt-1" />
                  <a href="tel:+918709742352">+91 8709742352</a>
                </li>

                <li className="d-flex mb-2">
                  <FaEnvelope className="me-2 text-golden mt-1" />
                  <a href="mailto:info@cafesero.in">info@cafesero.in</a>
                </li>

                <li className="d-flex">
                  <FaMapMarkerAlt className="me-2 text-golden mt-1" />
                  <span>Delhi, Gurgaon ...</span>
                </li>
              </ul>
            </div>
          </div>

          <hr className="footer-divider my-4" />

          <div className="text-center small text-golden mb-2">
            <strong>
              {" "}
              © {new Date().getFullYear()} CAFESERO | All Rights Reserved.
            </strong>
          </div>

          {/* <div className="d-md-flex justify-content-between small text-center">
            <div className="text-light-cafe">
              © {new Date().getFullYear()} CafeSero Pvt Ltd.
            </div>
            <div>
              <Link to="/privacy-policy">Privacy</Link> |
              <Link to="/terms-conditions"> Terms</Link>
            </div>
          </div> */}
        </div>

        <PurchasePopup products={allServices} />

        {/* WhatsApp Floating */}
        <a
          href="https://wa.me/918709742352"
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-float"
        >
          <FaWhatsapp size={24} />
        </a>
      </footer>
    </>
  );
}
